import type { Channel, CustomRole, Message, NotifyLevel, TeamMember } from '@nexo/types';
import { CHANNEL_MENTION_PATTERN, mentionedRoleIds, mentionedUserIds } from '@nexo/types';
import { alertPrefs } from './alerts';

export type EffectiveLevel = Exclude<NotifyLevel, 'default'> | 'muted';

export const isMuted = (channel: Channel, now = new Date()) =>
  channel.mutedUntil !== null && new Date(channel.mutedUntil) > now;

/** What a conversation should do for you right now (DMs default to everything). */
export function effectiveLevel(channel: Channel, now = new Date()): EffectiveLevel {
  if (isMuted(channel, now)) return 'muted';
  if (channel.notifyLevel !== 'default') return channel.notifyLevel;
  if (channel.kind === 'dm') return 'all';
  return alertPrefs.get().scope === 'mentions' ? 'mentions' : 'all';
}

/** A mention in Discord's sense: @you, @canal, one of your roles, a reply to you or a DM. */
export function mentionsMe(
  message: Message,
  channel: Channel | undefined,
  me: { id: string; members: TeamMember[]; roles: CustomRole[] },
): boolean {
  if (channel?.kind === 'dm') return true;
  const myRoles = me.members.find((m) => m.id === me.id)?.customRoleIds ?? [];
  return (
    mentionedUserIds(message.body).includes(me.id) ||
    CHANNEL_MENTION_PATTERN.test(message.body) ||
    message.replyTo?.author.id === me.id ||
    mentionedRoleIds(message.body).some(
      (id) => myRoles.includes(id) && me.roles.find((r) => r.id === id)?.mentionable,
    )
  );
}

/** Red badge count of a conversation (0 when it notifies nothing). */
export function badgeCount(channel: Channel): number {
  const level = effectiveLevel(channel);
  return level === 'none' ? 0 : channel.mentionCount;
}

/** Whether the conversation shows as unread (bold + dot) in the sidebar. */
export function showsUnread(channel: Channel): boolean {
  const level = effectiveLevel(channel);
  return channel.unreadCount > 0 && level !== 'muted' && level !== 'none';
}
